import { currencyKey } from '@nxt-insurance/constants'
import type { Language, SupportedMarketKey } from '@nxt-insurance/constants'

import type { FormattingLocale } from './types'

interface Dependencies {
  getCurrentLanguage: () => Language
  getCurrentMarket: () => SupportedMarketKey
}

export class IntlFormatter {
  private dependencies: Dependencies
  constructor(dependencies: Dependencies) {
    this.dependencies = dependencies
  }

  private getDependency = <TDependencyKey extends keyof Dependencies>(
    key: TDependencyKey
  ): Dependencies[TDependencyKey] => {
    return this.dependencies[key]
  }

  getLocaleString = (
    marketKey: SupportedMarketKey = this.getDependency('getCurrentMarket')(),
    language: Language = this.getDependency('getCurrentLanguage')()
  ): FormattingLocale => `${language}-${marketKey}` // The BCP 47 (https://tools.ietf.org/html/rfc5646#section-2.1.1) language tag is case insensitive

  getEnforcedFormattingLocale = (): FormattingLocale => {
    const localeString = this.getLocaleString()
    const isEnglishLanguage = localeString.startsWith('en-')
    if (isEnglishLanguage) {
      return 'en-de'
    }
    return localeString
  }

  private getNumberFormatter = (options?: Omit<Intl.NumberFormatOptions, 'style'>) => {
    const localeString = this.getEnforcedFormattingLocale()
    return new Intl.NumberFormat(localeString, {
      style: 'decimal',
      ...options,
    })
  }

  private getCurrencyFormatter = (options?: Omit<Intl.NumberFormatOptions, 'style'>) => {
    const localeString = this.getEnforcedFormattingLocale()

    const market = this.getDependency('getCurrentMarket')()

    return new Intl.NumberFormat(localeString, {
      currencyDisplay: 'symbol',
      style: 'currency',
      currency: currencyKey[market],
      minimumFractionDigits: 0,
      ...options,
    })
  }

  private getPercentFormatter = (options?: Omit<Intl.NumberFormatOptions, 'style'>) => {
    const localeString = this.getEnforcedFormattingLocale()
    return new Intl.NumberFormat(localeString, {
      style: 'percent',
      ...options,
    })
  }

  getNumberSeparators = (): {
    thousandSeparator: string
    decimalSeparator: string
  } => {
    const formatterFn = this.getNumberFormatter()
    const thousandSeparator = formatterFn.format(1111).replaceAll('1', '')
    const decimalSeparator = formatterFn.format(1.1).replaceAll('1', '')
    return { thousandSeparator, decimalSeparator }
  }

  formatNumber = (
    value: number | bigint,
    options?: Omit<Intl.NumberFormatOptions, 'style'>
  ): string => {
    const numberFormatter = this.getNumberFormatter(options)
    return numberFormatter.format(value)
  }

  formatCurrency = (
    value: bigint | number | string,
    options?: Omit<Intl.NumberFormatOptions, 'style'>
  ): string => {
    const currencyFormatter = this.getCurrencyFormatter(options)

    const amount: bigint | number =
      typeof value === 'string' ? Number(value.replace(',', '.')) : value
    return currencyFormatter.format(amount)
  }

  formatPrice = (
    value: number | string,
    options?: Omit<Intl.NumberFormatOptions, 'style' | 'minimumFractionDigits'>
  ): string => {
    const priceOptions = {
      ...options,
      minimumFractionDigits: 2,
    }
    return this.formatCurrency(value, priceOptions)
  }

  formatPercent = (
    value: number | string,
    options?: Omit<Intl.NumberFormatOptions, 'style'>
  ): string => {
    const amount = typeof value === 'string' ? Number(value.replace(',', '.')) : Number(value)
    const percentFormatter = this.getPercentFormatter(options)
    return percentFormatter.format(amount / 100)
  }

  parseFormattedNumber = (stringNumber: string): number => {
    const { thousandSeparator, decimalSeparator } = this.getNumberSeparators()

    return Number.parseFloat(
      stringNumber
        .replaceAll(new RegExp(`\\${thousandSeparator}`, 'g'), '')
        .replace(new RegExp(`\\${decimalSeparator}`), '.')
    )
  }

  formatToLocaleDate = (dateString: string): string => {
    const date = new Date(dateString)
    const locale = this.getEnforcedFormattingLocale()
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }
    return date.toLocaleDateString(locale, options)
  }
}
